import { colorFor, longDay } from '@hudhuria/shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { type EntryKind, useTodayView } from '~/api/derived';
import { useCan, useCenter } from '~/api/queries';
import { ExcelFileIcon } from '~/components/icons';
import { Avatar, Chip, EmptyState } from '~/components/ui';

export const Route = createFileRoute('/_app/_tabs/timeline')({
  validateSearch: z.object({ f: z.enum(['all', 'in', 'group', 'new']).optional() }),
  component: Timeline,
});

const FILTERS = [
  ['all', 'All'],
  ['in', 'Walk-ins'],
  ['group', 'Groups'],
  ['new', 'New'],
] as const;

const TAGS: Record<EntryKind, { label: string; bg: string; fg: string }> = {
  in: { label: 'IN', bg: '#e6f4fc', fg: '#0369a1' },
  new: { label: 'NEW', bg: '#ecfdf5', fg: '#047857' },
  group: { label: 'GROUP', bg: '#fef3c7', fg: '#b45309' },
};

function Timeline() {
  const navigate = useNavigate();
  const { f = 'all' } = Route.useSearch();
  const center = useCenter();
  const view = useTodayView();
  const canExport = useCan('export');
  const entries = view.entries.filter((e) => f === 'all' || e.kind === f);

  return (
    <div className="flex min-h-0 flex-1 animate-fade-in-fast flex-col">
      <div className="shrink-0 border-b border-line-soft px-[22px] pt-safe-6 pb-4">
        <div className="flex items-start justify-between gap-[10px]">
          <div className="min-w-0">
            <div className="truncate text-[13px] text-slate-500">
              {longDay(new Date(), view.tz)} · {center?.name}
            </div>
            <div className="mt-0.5 font-display text-[30px] font-bold tracking-[-0.02em]">
              {view.present.size} in today
            </div>
          </div>
          {canExport && (
            <button
              type="button"
              onClick={() => void navigate({ to: '/export' })}
              className="flex shrink-0 cursor-pointer items-center gap-1.5 rounded-[11px] border-none bg-brand-50 px-[11px] py-2 text-[12.5px] font-bold text-brand"
            >
              <ExcelFileIcon />
              Excel
            </button>
          )}
        </div>
        <div className="mt-[14px] flex gap-[7px] scroll-x">
          {FILTERS.map(([id, label]) => (
            <Chip
              key={id}
              on={f === id}
              onClick={() =>
                void navigate({ to: '.', search: { f: id === 'all' ? undefined : id }, replace: true })
              }
            >
              {label}
            </Chip>
          ))}
        </div>
      </div>

      <div className="flex-1 px-5 pt-[18px] pb-nav scroll-y">
        <div className="mb-1.5 grid grid-cols-[44px_22px_minmax(0,1fr)] items-center gap-2">
          <div className="text-right font-display text-[12px] font-bold text-red-500">now</div>
          <div className="flex justify-center">
            <div className="h-[9px] w-[9px] rounded-full bg-red-500" />
          </div>
          <div className="h-0.5 bg-red-500" />
        </div>
        {entries.map((e, i) => {
          const tag = TAGS[e.kind];
          const isGroup = e.kind === 'group';
          return (
            <div
              key={e.id}
              className="grid animate-fade-up grid-cols-[44px_22px_minmax(0,1fr)] items-stretch gap-2"
            >
              <div
                className="pt-[15px] text-right font-display text-[12px] font-semibold"
                style={{ color: i === 0 ? '#0f172a' : '#94a3b8' }}
              >
                {e.time}
              </div>
              <div className="relative flex justify-center">
                <div className="absolute top-0 bottom-0 w-0.5 bg-rail" />
                <div
                  className="relative mt-[14px] h-[13px] w-[13px] rounded-full border-[2.5px] border-white"
                  style={{ background: isGroup ? '#f59e0b' : colorFor(e.name) }}
                />
              </div>
              <div className="pb-[11px]">
                <button
                  type="button"
                  disabled={!e.studentId}
                  onClick={() =>
                    e.studentId &&
                    void navigate({ to: '/student/$studentId', params: { studentId: e.studentId } })
                  }
                  className="flex w-full cursor-pointer items-center gap-[11px] rounded-[15px] border border-line bg-white px-[13px] py-3 text-left disabled:cursor-default"
                >
                  {isGroup ? (
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[11px] bg-amber-500 text-[12.5px] font-bold text-white">
                      G
                    </div>
                  ) : (
                    <Avatar name={e.name} photoUrl={e.photoUrl} size={36} radius={11} fontSize={12.5} />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[14px] font-semibold">{e.name}</div>
                    <div className="mt-px truncate text-[12px] text-slate-400">{e.purpose}</div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <div
                      className="rounded-md px-2 py-[3px] text-[10.5px] font-bold"
                      style={{ background: tag.bg, color: tag.fg }}
                    >
                      {tag.label}
                    </div>
                    {e.pending && (
                      <div className="text-[10px] font-semibold text-amber-600">waiting to sync</div>
                    )}
                  </div>
                </button>
              </div>
            </div>
          );
        })}
        {view.ready && entries.length === 0 && (
          <EmptyState>
            {f === 'all'
              ? 'Nothing yet today — check-ins appear here as they happen.'
              : 'Nothing in this filter yet today.'}
          </EmptyState>
        )}
      </div>
    </div>
  );
}
